import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import propTypes from 'prop-types';
import { useMemo } from 'react';

import { NS } from '@/constants';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { useAppDispatch, useAppSelector } from '@/redux';
import { showFullScreen } from '@/redux/slices/gui.slice';
import { CallToActionKind } from '@/types';

import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';

import styles from './CategFiltersMobile.module.scss';
import { FULL_SCREEN_CONTAINER_MOBILE } from '@/constants/gui';
import { URLS } from '@/constants/urls';

const FilterMobile = dynamic(() => import('@/components/categ/filters/FilterMobile'), { ssr: false });
FilterMobile.displayName = 'FilterMobile';
const SortMobile = dynamic(() => import('@/components/categ/filters/SortMobile'), { ssr: false });
SortMobile.displayName = 'SortMobile';

const CategFiltersMobile = ({
  filters,
  deleteFilter,
  deleteAll,
  onSortChange,
  onFilterChange,
  sort,
  selectedFiltersInfos,
  withPadding,
  isProducerCategory = false,
  showCUProducts,
  onlyCUChecked,
  isFromWB = false
}) => {
  const { t } = useTranslation([NS.COMMON, NS.CATEG]);
  const dispatch = useAppDispatch();
  const { isFilterMobileVisible, isSortMobileVisible } = useAppSelector(({ gui }) => gui);
  const isMobile = useScreenSizeContext().isMobile;

  const mobileFilters = useMemo(() => filters?.filter((filter) =>
    filter.displayable && (filter.nbByValue.length > 1 || selectedFiltersInfos.some((s) => s.nameId === filter.nameId))
  ), [filters]);

  const topFiveFilters = useMemo(() => mobileFilters?.slice(0, 5), [mobileFilters, selectedFiltersInfos]);

  const showFilters = () => (
    mobileFilters?.length > 0 &&
    dispatch(showFullScreen({ fullScreenType: FULL_SCREEN_CONTAINER_MOBILE.FILTER, onShow: undefined, isMobile }))
  );

  const customClassName = [
    styles.container,
    withPadding && styles['with-padding']
  ].filter(Boolean).join(' ');

  return (
    <>
      <div className={customClassName}>
        <div className={styles.buttons} data-rbf="categ-filters-mobile">
          <CallToActionContainer
            kind={CallToActionKind.SECONDARY}
            fullWidth
            onClick={() => showFilters()}
            icon={{
              url: URLS.ICON_FILTERS,
              alt: t('alter:filterProducts'),
              width: 16,
              height: 16
            }}
          >
            {t('btn.filter')}
          </CallToActionContainer>
          {isMobile && topFiveFilters?.length > 0 && topFiveFilters.map((filter, i) => (
            <CallToActionContainer
              key={i}
              data-test-id="top-filter"
              kind={CallToActionKind.SECONDARY}
              fullWidth
              onClick={() => dispatch(showFullScreen({
                fullScreenType: FULL_SCREEN_CONTAINER_MOBILE.FILTER,
                selectedFilter: filter, isMobile
              }))}
            >
              {filter.name}
            </CallToActionContainer>
          ))}
          <CallToActionContainer
            kind={CallToActionKind.SECONDARY}
            fullWidth
            onClick={() => dispatch(
              showFullScreen({ fullScreenType: FULL_SCREEN_CONTAINER_MOBILE.SORT, onShow: undefined, isMobile })
            )}
          >
            {t('category:sort')}
          </CallToActionContainer>
        </div>
      </div>
      {isFilterMobileVisible && (
        <FilterMobile
          onChange={onFilterChange}
          onDelete={deleteFilter}
          deleteAll={deleteAll}
          selectedFiltersInfos={selectedFiltersInfos}
          filters={mobileFilters}
          showCUProducts={showCUProducts}
          onlyCUChecked={onlyCUChecked}
          isFromWB={isFromWB}
          sort={sort}
        />
      )}
      {isSortMobileVisible && (
        <SortMobile selectedSort={sort} onChange={onSortChange} isProducerCategory={isProducerCategory} />
      )}
    </>
  );
};

CategFiltersMobile.propTypes = {
  filters: propTypes.array,
  deleteFilter: propTypes.func,
  deleteAll: propTypes.func,
  onSortChange: propTypes.func,
  onFilterChange: propTypes.func,
  sort: propTypes.number,
  selectedFiltersInfos: propTypes.object,
  withPadding: propTypes.bool,
  showCUProducts: propTypes.func,
  onlyCUChecked: propTypes.bool,
  isFromWB: propTypes.bool
};

export default CategFiltersMobile;
